import { CatchError } from 'Exceptions/exceptions'
import ApiTrafficLogsService from 'Main/service/ApiTrafficLogsService'
import { minPageSizeConfigs } from 'Main/Utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getRefreshToken } from 'variables/common'
import ApiTrafficLogsToolbar from 'views/components/Bar/ApiTrafficLogsToolbar'
import DatalogPagination from 'views/components/Datalog/DatalogPagination'
import ApiTrafficLogsTable from 'views/components/Table/ApiTrafficLogsTable'
import useLogUserFilter from 'views/dashboard/logs/useLogUserFilter'

function ApiTrafficLogsList () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const apiTrafficLogsService = useMemo(() => new ApiTrafficLogsService(), [])
  const { isSuperAdmin, users, loadingUsers } = useLogUserFilter()

  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [endpointOptions, setEndpointOptions] = useState([])

  const [currentPage, setPage] = useState(1)
  const [totalPage, setTotalPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [pageSize, setPageSize] = useState(minPageSizeConfigs)

  const [filterEndpoint, setFilterEndpoint] = useState('%')
  const [filterStatus, setFilterStatus] = useState('%')
  const [filterUserId, setFilterUserId] = useState('ALL')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const fetchLogs = useCallback(
    async ({
      page = 1,
      size = minPageSizeConfigs,
      endpoint = '%',
      status = '%',
      from = '',
      to = '',
      userId = 'ALL'
    } = {}) => {
      try {
        setLoading(true)
        setError(null)

        const res = await apiTrafficLogsService.getData(page, size, endpoint, status, from, to, userId)
        const data = res.data

        setLogs(data?.result || [])
        setTotalPage(data?.totalPage || 1)
        setTotalRecords(data?.max || 0)

        // Fall back to deriving endpoint options from the current page if the
        // dedicated /endpoints lookup below hasn't populated them yet.
        const derived = Array.from(new Set((data?.result || []).map((el) => el.endpoint).filter(Boolean))).sort()
        setEndpointOptions((current) => current.length ? current : derived)
      } catch (err) {
        const caught = CatchError(err, navigate, refreshToken)
        if (caught !== undefined) setError('Failed to load API traffic logs.')
      } finally {
        setLoading(false)
      }
    },
    [apiTrafficLogsService, navigate, refreshToken]
  )

  useEffect(() => {
    fetchLogs()
    // Prefer the distinct, server-wide endpoint list over page-derived options.
    apiTrafficLogsService
      .getEndpoints('ALL')
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setEndpointOptions(res.data)
      })
      .catch(() => {
        /* keep the page-derived fallback from fetchLogs */
      })
  }, [apiTrafficLogsService, fetchLogs])

  const handleView = (id) => {
    navigate(`/dashboard/api-traffic/${id}`)
  }

  const handleEndpointChange = (e) => {
    const val = e.target.value
    setFilterEndpoint(val)
    setPage(1)
    fetchLogs({ page: 1, size: pageSize, endpoint: val, status: filterStatus, from: dateFrom, to: dateTo, userId: filterUserId })
  }

  const handleStatusChange = (e) => {
    const val = e.target.value
    setFilterStatus(val)
    setPage(1)
    fetchLogs({ page: 1, size: pageSize, endpoint: filterEndpoint, status: val, from: dateFrom, to: dateTo, userId: filterUserId })
  }

  const handlePageSizeChange = (e) => {
    const val = parseInt(e.target.value, 10)
    setPageSize(val)
    setPage(1)
    fetchLogs({ page: 1, size: val, endpoint: filterEndpoint, status: filterStatus, from: dateFrom, to: dateTo, userId: filterUserId })
  }

  const handleDateChange = ({ from, to }) => {
    setDateFrom(from)
    setDateTo(to)
    setPage(1)
    fetchLogs({ page: 1, size: pageSize, endpoint: filterEndpoint, status: filterStatus, from, to, userId: filterUserId })
  }

  const handleUserChange = (e) => {
    const userId = e.target.value
    setFilterUserId(userId)
    setFilterEndpoint('%')
    setEndpointOptions([])
    setPage(1)
    fetchLogs({ page: 1, size: pageSize, endpoint: '%', status: filterStatus, from: dateFrom, to: dateTo, userId })
    apiTrafficLogsService
      .getEndpoints(userId)
      .then((res) => setEndpointOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEndpointOptions([]))
  }

  const handleClear = () => {
    setFilterEndpoint('%')
    setFilterStatus('%')
    setFilterUserId('ALL')
    setDateFrom('')
    setDateTo('')
    setPage(1)
    fetchLogs({ page: 1, size: pageSize, endpoint: '%', status: '%', from: '', to: '', userId: 'ALL' })
    apiTrafficLogsService
      .getEndpoints('ALL')
      .then((res) => setEndpointOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEndpointOptions([]))
  }

  const handlePageChange = (page) => {
    setPage(page)
    fetchLogs({ page, size: pageSize, endpoint: filterEndpoint, status: filterStatus, from: dateFrom, to: dateTo, userId: filterUserId })
  }

  return (
    <div className="page-content">
      <section>
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <i className="fa-solid fa-arrow-right-arrow-left me-1" /> API Traffic Logs
            </h5>
            <span className="text-white">
              Total <strong>{totalRecords || 0}</strong> logs
            </span>
          </div>

          <div className="card-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="fa fa-exclamation-circle me-1" />
                {error}
              </div>
            )}

            <ApiTrafficLogsToolbar
              filterEndpoint={filterEndpoint}
              filterStatus={filterStatus}
              pageSize={pageSize}
              endpointOptions={endpointOptions}
              filterUserId={filterUserId}
              showUserFilter={isSuperAdmin}
              users={users}
              loadingUsers={loadingUsers}
              onUserChange={handleUserChange}
              onEndpointChange={handleEndpointChange}
              onStatusChange={handleStatusChange}
              onPageSizeChange={handlePageSizeChange}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={handleDateChange}
              onClear={handleClear}
            />

            <ApiTrafficLogsTable logs={logs} loading={loading} onView={handleView} />

            <div className="mt-3">
              <DatalogPagination
                currentPage={currentPage}
                totalPage={totalPage}
                totalRecords={totalRecords}
                pageSize={pageSize}
                loading={loading}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ApiTrafficLogsList
